import { useEffect, useState, useRef } from "react";
import api from "./api";
import { brandName } from "./brand";

const POLL_MS = 60_000; // poll every 60s
const STORAGE_KEY = "hv.lastSeenTicketAt";

/**
 * Polls /api/tickets and tracks how many tickets were created after the user's
 * personal "last seen" timestamp (per-user via auth header).
 *
 * Returns: { newCount, markAsSeen, latest, requestPermission, permission }
 *
 * Side-effect: when newCount > previous newCount, fires a desktop notification
 * (if permission granted).
 */
export function useTicketNotifications({ userId, role } = {}) {
  const [tickets, setTickets] = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(() =>
    parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10),
  );
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const lastSeenIdsRef = useRef(new Set());

  // Only run for staff (not clients)
  const enabled = role && role !== "client";
  // Admin/owner/manager own the triage queue → unassigned tickets count.
  // Reps/techs only count tickets assigned to them.
  const isTriager = ["owner", "admin", "manager"].includes(role);

  useEffect(() => {
    if (!enabled) return undefined;
    let aborted = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/tickets");
        if (aborted) return;
        // [Iter 290zp.17] Defensive guard — axios occasionally resolves
        // with non-array bodies (304 cached payloads, intermediate proxy
        // responses, transient tenant-routing edge cases). Without this
        // check, every render below crashes with
        // `tickets.filter is not a function` and the entire app is
        // unmountable. Keep it strict — anything not actually an array
        // is treated as "no data this poll".
        const safe = Array.isArray(data) ? data : [];
        setTickets(safe);

        // Detect brand-new tickets we haven't seen this session and fire desktop notif.
        const cur = new Set(safe.map((t) => t.id));
        // Bug fix (2026-05-13 — Charmaine): only count tickets that are
        // actionable BY THIS USER. Otherwise an admin sees a "new ticket"
        // badge for a ticket already assigned to someone else.
        const _isActionable = (t) => {
          if (t.assigned_to_id && t.assigned_to_id === userId) return true;
          if (isTriager && !t.assigned_to_id) return true;
          return false;
        };
        const newOnes = safe.filter(
          (t) =>
            !lastSeenIdsRef.current.has(t.id) &&
            new Date(t.created_at).getTime() > lastSeenAt &&
            lastSeenIdsRef.current.size > 0 && // skip first poll
            _isActionable(t),
        );
        lastSeenIdsRef.current = cur;

        if (newOnes.length > 0 && permission === "granted") {
          newOnes.slice(0, 3).forEach((t) => {
            try {
              const n = new Notification(`${brandName()} · New ticket #${t.short_id || t.id.slice(0, 6)}`, {
                body: t.subject || "Open the dashboard to view",
                icon: "/favicon.ico",
                tag: `ticket-${t.id}`,
              });
              n.onclick = () => {
                window.focus();
                window.location.href = `/tickets/${t.id}`;
              };
            } catch (_) {
              /* ignore */
            }
          });
        }
      } catch (_) {
        /* keep polling */
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      aborted = true;
      clearInterval(id);
    };
  }, [enabled, lastSeenAt, permission, userId, isTriager]);

  // Same actionability gate on the persistent badge count — so an admin's
  // sidebar Tickets badge only shows when there's actually something for
  // them to do (assigned to them, or unassigned awaiting triage).
  const isActionable = (t) => {
    if (t.assigned_to_id && t.assigned_to_id === userId) return true;
    if (isTriager && !t.assigned_to_id) return true;
    return false;
  };
  // [Iter 290zp.17] Final safety net: even though setTickets above is
  // guarded, this defends against any future caller that wires a
  // non-array initial state. Cheap, defensive, regression-proof.
  const ticketsArr = Array.isArray(tickets) ? tickets : [];
  const newOnes = ticketsArr.filter(
    (t) =>
      new Date(t.created_at).getTime() > lastSeenAt &&
      ["logged", "pending_assignment", "open"].includes(t.status) &&
      isActionable(t),
  );

  const markAsSeen = () => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, String(now));
    setLastSeenAt(now);
  };

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return "denied";
    const p = await Notification.requestPermission();
    setPermission(p);
    return p;
  };

  return {
    newCount: newOnes.length,
    newTickets: newOnes,
    markAsSeen,
    permission,
    requestPermission,
  };
}
